import { useEffect, useRef, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import {
  selectEditMode,
  selectIsServiceWorkerRegistered,
} from "@/features/app/appSlice";
import { BuildingState } from "@/features/cartography/cartographySlice";
import { selectConfigItem } from "@/features/config/configSlice";

import type { SearchParamsSitum } from "@/features/app/types";
import {
  selectCurrentBuildingID,
  selectCurrentBuildingState,
} from "@/features/cartography/cartographySlice.selectors";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";
import { useAppSelector } from ".";
import { useRefListener } from "./useRefListener";
import useServiceWorker from "./useServiceWorker";

const useRefresh = () => {
  // Redux
  const refreshRate = useAppSelector(selectConfigItem("internal.refreshRate"));
  const refreshDate = useAppSelector(selectConfigItem("internal.refreshDate"));
  const limitedRefresh = useAppSelector(
    selectConfigItem("internal.limitedRefresh"),
  );
  const tryRefreshOnFirstLoad = useAppSelector(
    selectConfigItem("internal.tryRefreshOnFirstLoad"),
  );
  const currentBuildingID = useAppSelector(selectCurrentBuildingID);
  const currentBuildingState = useAppSelector(selectCurrentBuildingState);
  const isServiceWorkerRegistered = useAppSelector(
    selectIsServiceWorkerRegistered,
  );
  const editMode = useAppSelector(selectEditMode);

  // Hooks
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { refreshData, hasCurrentBuildingDataChanged } = useSitumAPIContext();
  const { refreshAPICache, finishAPIRefresh } = useServiceWorker();

  // State
  const [initialRefreshDone, setInitialRefreshDone] = useState(false);

  const profileUrl =
    searchParams.get("config_file" satisfies SearchParamsSitum) ||
    params.config_file ||
    null; // TODO deprecate
  const profileDomain =
    searchParams.get("config_domain" satisfies SearchParamsSitum) || null;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useRefListener(() => {
    // Avoid refresh while editing
    if (editMode !== undefined) return;
    const performRefresh = async () => {
      console.debug(`Performing refresh now`);
      refreshData({
        profileDomain,
        profileUrl,
        searchParams,
      }).finally(() => {
        finishAPIRefresh();
      });
    };
    // Without service worker refresh normally
    if (!isServiceWorkerRegistered) {
      performRefresh();
      return;
    }
    refreshAPICache()
      .then(() => {
        if (limitedRefresh) {
          hasCurrentBuildingDataChanged()
            .then(performRefresh)
            .catch(() => {
              finishAPIRefresh();
            });
        } else {
          performRefresh();
        }
      })
      .catch((e) => {
        console.error(`Could not refresh data ${e}`);
        finishAPIRefresh();
      });
  }, [
    editMode,
    refreshAPICache,
    refreshData,
    searchParams,
    profileUrl,
    profileDomain,
    limitedRefresh,
    currentBuildingID,
    hasCurrentBuildingDataChanged,
    isServiceWorkerRegistered,
  ]);

  const scheduleRefresh = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!refreshDate.hour) return;
    const currentMoment = new Date();
    const [h, m] = refreshDate.hour.split(":").map(Number);

    const refreshMoment = new Date();
    refreshMoment.setHours(h);
    refreshMoment.setMinutes(m);
    refreshMoment.setMilliseconds(0);

    const timeDiffToExecute = refreshMoment.getTime() - currentMoment.getTime();
    if (timeDiffToExecute < 0) {
      console.debug(`=> Todays refresh already happened`);
      if (refreshDate.repeat) {
        console.debug(`=> Gonna schedule 1 execution for tomorrow`);
        const tomorrowUpdate = new Date();
        tomorrowUpdate.setDate(currentMoment.getDate() + 1);
        tomorrowUpdate.setHours(h);
        tomorrowUpdate.setMinutes(m);
        const timeDiffToExecuteTomorrow =
          tomorrowUpdate.getTime() - currentMoment.getTime();
        console.debug(
          `=> Scheduled refresh in: ${(
            timeDiffToExecuteTomorrow / 1000 / 60 / 60
          ).toFixed(2)} hours`,
        );

        timeoutRef.current = setTimeout(() => {
          refresh.current();
          refreshDate.repeat && scheduleRefresh();
        }, timeDiffToExecuteTomorrow);
      }
      return;
    }

    console.debug(
      `=> Scheduled refresh in: ${timeDiffToExecute / 1000 / 60} min`,
    );

    timeoutRef.current = setTimeout(() => {
      refresh.current();
      refreshDate.repeat && scheduleRefresh();
    }, timeDiffToExecute);
  };

  //refresh info
  useEffect(() => {
    if (!refreshRate) return;

    console.debug(
      `Performing refresh every ${refreshRate} h / ${60 * refreshRate} m / ${
        60 * 60 * refreshRate
      } s`,
    );
    const intervalId = setInterval(
      () => {
        refresh.current();
      },
      1000 * 60 * 60 * refreshRate,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshRate]);

  useEffect(() => {
    scheduleRefresh();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [refreshDate]);

  // Trigger refresh when selected building finishes
  useEffect(() => {
    if (
      !tryRefreshOnFirstLoad ||
      initialRefreshDone ||
      currentBuildingState === BuildingState.PARTIAL
    ) {
      return;
    }
    refresh.current();
    setInitialRefreshDone(true);
  }, [currentBuildingState]);
};

export default useRefresh;
